import { useTheme } from "@emotion/react";
import { TextField } from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
  type DatePickerProps,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useState, type ReactNode } from "react";

import { FormFieldInformation } from "@aviary/components/FormFieldInformation";
import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";

import { DeprecatedDatePicker } from "./DeprecatedDatePicker";

import * as styles from "./DatePicker.styles";

interface Props extends Omit<DatePickerProps<Date, Date>, "renderInput"> {
  label: string;
  value: Date;
  variant: "dialog" | "inline";
  onChange: (date: Date) => void;
  isInTheFuture?: boolean;
  errors?: ReactNode[];
  required?: boolean;
}

const DatePicker = (props: Props) => {
  const { variant, isInTheFuture, errors, value, required, ...rest } = props;

  const [isOpen, setIsOpen] = useState(false);
  const currentTheme = useTheme();
  const isDS4Enabled = useIsDS4Enabled();

  if (!isDS4Enabled) return <DeprecatedDatePicker {...props} />;

  const portalProps =
    variant === "inline"
      ? { PopperProps: { disablePortal: true } }
      : { DialogProps: { disablePortal: true } };

  const dateRangeProps = isInTheFuture ? { minDate: new Date() } : { maxDate: new Date() };

  const hasErrors = () => {
    if (!errors) return false;

    return errors.filter(e => !!e).length > 0;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        {...{
          variant,
          ...portalProps,
          ...dateRangeProps,
        }}
        openTo={value ? "day" : "year"}
        value={value}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        views={["year", "month", "day"]}
        renderInput={muiProps => {
          return (
            <div css={styles.DatePickerWrapper}>
              <TextField
                {...muiProps}
                sx={styles.textField(currentTheme)}
                id="mui-theme-provider-standard-input"
                variant="filled"
                fullWidth
                placeholder="yyyy-mm-dd"
                helperText={null}
                error={hasErrors()}
                required={required}
              />
              <FormFieldInformation errors={errors} />
            </div>
          );
        }}
        {...rest}
      />
    </LocalizationProvider>
  );
};

export { DatePicker };
